import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { empty_pagination } from '../../constants/empty_pagination';
import Agent from '../agent';

const initialState = {
  loading: false,
  contracts: [],
  singleContract: {},
  pagination: { pages: 0 },
  errors: {},
  redirectTo: null,
  success: false,
  message: '',
};

// These are redux/toolkit async thunk for performing api call
// There methods are automatically attached to reducers and actions below
export const getContracts = createAsyncThunk(
  'users/contracts',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await Agent.Contract.get(payload);
      return data;
    } catch (err) {
      let error = err;
      if (!error.response) {
        return rejectWithValue({ errors: { [err.name]: err.message } });
      }
      return rejectWithValue(error.response.body);
    }
  }
);

export const addNewContract = createAsyncThunk(
  'contracts/create',
  async (data, { rejectWithValue }) => {
    try {
      return await (
        await Agent.Contract.create(data)
      ).data;
    } catch (err) {
      let error = err;
      if (!error.response) {
        return rejectWithValue({ errors: { [err.name]: err.message } });
      }
      return rejectWithValue(error.response.body);
    }
  }
);

export const updateContract = createAsyncThunk(
  'contracts/update',
  async (data, { rejectWithValue }) => {
    try {
      return await (
        await Agent.Contract.update(data)
      ).data;
    } catch (err) {
      let error = err;
      if (!error.response) {
        return rejectWithValue({ errors: { [err.name]: err.message } });
      }
      return rejectWithValue(error.response.body);
    }
  }
);

export const findContractById = createAsyncThunk(
  'contract/get',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await Agent.Contract.getOne(id);
      return data;
    } catch (err) {
      let error = err;
      if (!error.response) {
        return rejectWithValue({ errors: { [err.name]: err.message } });
      }
      return rejectWithValue(error.response.body);
    }
  }
);

export const deleteContract = createAsyncThunk(
  'contracts/delete',
  async (id, { rejectWithValue }) => {
    try {
      return await (
        await Agent.Contract.delete(id)
      ).data;
    } catch (err) {
      let error = err;
      if (!error.response) {
        return rejectWithValue({ errors: { [err.name]: err.message } });
      }
      return rejectWithValue(error.response.body);
    }
  }
);

const contractsSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    setLoading(state) {
      state.loading = true;
    },
    updateUserState(state, action) {
      if (state.errors[action.payload.key]) delete state.errors[action.payload.key];
      state.user[action.payload.key] = action.payload.value;
    },
    setSuccess(state) {
      state.success = false;
    },
  },
  extraReducers: {
    // these are the actions generated by the above createAsyncThunk of redux/toolkit
    [getContracts.pending]: (state) => {
      state.loading = true;
      state.success = false;
    },

    [getContracts.fulfilled]: (state, action) => {
      let pagination = action?.payload?.pagination || empty_pagination;
      let contracts = action?.payload?.data || [];
      state.loading = false;
      state.success = false;
      state.contracts = pagination.currentPage > 1 ? [...state.contracts, contracts] : contracts;
      state.pagination = pagination;
    },

    [getContracts.rejected]: (state, action) => {
      state.loading = false;
      state.success = false;
      state.errors = action?.payload?.errors || {};
    },

    // Add New Contract
    [addNewContract.pending]: (state) => {
      state.loading = true;
      state.success = false;
    },

    [addNewContract.fulfilled]: (state, action) => {
      let contracts = action?.payload?.data || {};
      state.loading = false;
      state.success = true;
      state.message = 'Contract successfully created';
      state.contracts = [...state.contracts, contracts];
    },

    [addNewContract.rejected]: (state, action) => {
      state.loading = false;
      state.success = false;
      state.errors = action?.payload?.errors || {};
    },

    // find Contract By Id
    [findContractById.pending]: (state) => {
      state.loading = true;
      state.success = false;
    },

    [findContractById.fulfilled]: (state, action) => {
      state.loading = false;
      state.success = false;
      state.singleContract = action?.payload?.data || {};
    },

    [findContractById.rejected]: (state, action) => {
      state.loading = false;
      state.success = false;
      state.errors = action?.payload?.errors || {};
    },

    // update Contract
    [updateContract.pending]: (state) => {
      state.loading = true;
      state.success = false;
    },

    [updateContract.fulfilled]: (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = 'Contract successfully updated';
      const data = current(state.contracts);
      const index = data.findIndex((item) => {
        return item._id === action?.payload?.data?._id;
      });
      if (index === -1) return;

      let contract = action?.payload?.data || '';
      const final = [
        ...data.slice(0, index),
        ...[contract],
        ...data.slice(index + 1, data.length),
      ];
      state.contracts = final;
    },

    [updateContract.rejected]: (state, action) => {
      state.loading = false;
      state.success = false;
      state.errors = action?.payload?.errors || {};
    },

    // deleteHedgeFundUser
    [deleteContract.pending]: (state) => {
      state.loading = true;
      state.success = false;
    },

    [deleteContract.fulfilled]: (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = 'Contract successfully deleted';
    },

    [deleteContract.rejected]: (state, action) => {
      state.loading = false;
      state.success = false;
      state.errors = action?.payload?.errors || {};
    },
  },
});

export const contractsSelector = (state) => state.contracts;

export const { reducer, actions } = contractsSlice;
